import type { EdgeSeries } from '@projections/data';
import {
  buildReturnData,
  createRng,
  type ReturnData,
  validateReturnData,
} from '@projections/engine';

/**
 * Assembling the engine's `ReturnData` for a client-side run (PLAN.md M7 + BACKEND.md §4.13.1).
 *
 * `runMonteCarlo` draws its return sequences from a `ReturnData` — the contemporaneous cross-section
 * of asset-class returns (the correlation trick). The web app obtains one of two ways:
 *
 *  1. REAL series (preferred, when the API/series JSON is available): fetch compact `EdgeSeries`
 *     (`{ meta, dates[], values[] }`) — from the API `GET /series/:id`, or from a bundled JSON
 *     fixture for offline dev — then run them through the engine's PURE `buildReturnData` bridge.
 *     This is the SAME bridge the Worker uses, so a client-side run matches a server-side run.
 *     `@projections/data` is touched TYPE-ONLY here (`EdgeSeries`), never as a value import.
 *
 *  2. SYNTHETIC illustrative data (offline default, clearly labelled in the UI as illustrative):
 *     `syntheticReturnData(assetClasses)` builds a small, plausible annual cross-section so the app
 *     is dogfoodable with NO backend and NO data fixtures. This is NOT validated market history; it
 *     exists so the fan chart and stress comparison render end-to-end offline.
 *
 * The asset-class ids MUST match the plan's account `allocation` keys (the engine weights a drawn
 * matrix per account by those keys).
 */

/** Asset classes the synthetic dataset covers (line up with common plan `allocation` keys). */
export interface SyntheticAssetSpec {
  /** asset-class id == an account `allocation` key, e.g. `th_set_tr`, `global_acwi_tr`, `cash`. */
  readonly id: string;
  /** Mean annual real-ish return (decimal). */
  readonly mean: number;
  /** Annual volatility (decimal std-dev). */
  readonly vol: number;
}

/** Sensible defaults so an offline plan over the common sleeves renders immediately. */
export const DEFAULT_SYNTHETIC_ASSETS: readonly SyntheticAssetSpec[] = [
  { id: 'th_set_tr', mean: 0.08, vol: 0.22 },
  { id: 'global_acwi_tr', mean: 0.07, vol: 0.16 },
  { id: 'th_gov_bond_tr', mean: 0.03, vol: 0.06 },
  { id: 'cash', mean: 0.015, vol: 0.005 },
];

/**
 * Build a small SYNTHETIC, illustrative `ReturnData` (offline default). Deterministic given `seed`
 * so the offline experience is reproducible. NOT real market history — the UI must label runs over
 * this dataset as "illustrative".
 *
 * BUILD-AGENT STUB: fill the body — generate `years` rows of a correlated-ish cross-section
 * (a single shared market factor + per-asset idiosyncratic noise from a small seeded PRNG, then
 * `mean + vol * z`), populate `assetClasses`/`periods`/`rows`/`inflation`/`annual`, and return a
 * `validateReturnData`-clean `ReturnData`. Keep it dependency-free (reuse the engine's `createRng`).
 */
export function syntheticReturnData(options?: {
  readonly assets?: readonly SyntheticAssetSpec[];
  readonly years?: number;
  readonly seed?: number;
  readonly startYear?: number;
}): ReturnData {
  const assets = options?.assets ?? DEFAULT_SYNTHETIC_ASSETS;
  const years = options?.years ?? 60;
  const seed = options?.seed ?? 1;
  const startYear = options?.startYear ?? 1965;

  const rng = createRng(seed);
  // Box–Muller standard-normal draw off the engine's seeded uniform stream (deterministic).
  const gauss = (): number => {
    let u = rng.nextFloat();
    let v = rng.nextFloat();
    if (u < 1e-9) u = 1e-9;
    if (v < 1e-9) v = 1e-9;
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  };

  const assetClasses = assets.map((a) => a.id);
  const periods: string[] = [];
  const rows: number[][] = [];
  const inflation: number[] = [];
  const annual: number[] = [];

  // A single shared market factor each year couples the assets (cheap correlation), plus per-asset
  // idiosyncratic noise. NOT validated market history — illustrative only.
  for (let y = 0; y < years; y++) {
    const market = gauss();
    const row: number[] = [];
    for (const a of assets) {
      // Cash-like assets barely load on the market factor; equities load heavily.
      const beta = a.vol >= 0.1 ? 0.7 : 0.15;
      const z = beta * market + Math.sqrt(1 - beta * beta) * gauss();
      row.push(a.mean + a.vol * z);
    }
    periods.push(String(startYear + y));
    rows.push(row);
    // Modest, mean-3% inflation for the optional CPI-indexed streams.
    inflation.push(0.03 + 0.01 * gauss());
    // Equal-weight blend for the legacy scalar IID sampler.
    annual.push(row.reduce((s, r) => s + r, 0) / row.length);
  }

  const data: ReturnData = { assetClasses, periods, rows, inflation, annual };
  validateReturnData(data);
  return data;
}

/**
 * Build a real `ReturnData` from compact `EdgeSeries` via the engine's PURE bridge. Pass a map of
 * `series_id → EdgeSeries` (loaded from the API or a fixture) and the asset-class→series mappings
 * derived from the plan's allocation keys. Thin delegation to the engine — no new logic.
 *
 * BUILD-AGENT STUB: assemble the `BuildReturnDataOptions` (mappings + optional inflation/fx series)
 * and call `buildReturnData(seriesById, options)`. Keep the mapping derivation in `simulationClient`
 * so this stays a thin wrapper.
 */
export function returnDataFromSeries(
  seriesById: ReadonlyMap<string, EdgeSeries>,
  options: Parameters<typeof buildReturnData>[1],
): ReturnData {
  // Thin delegation to the engine bridge (accepts a ReadonlyMap of series_id → EdgeSeries).
  return buildReturnData(seriesById, options);
}
